/**
 * Predefined rule-based planning ranges for repair exposure & concern levels.
 *
 * These are intentionally broad and conservative. They are NOT contractor bids
 * and NOT property-specific. ZIP code, if provided, is captured for follow-up
 * context only — it does NOT call any external API. The ranges adjust slightly
 * by severity, urgency, and home age via deterministic multipliers.
 *
 * Currency: USD. All planning ranges are nationally-conservative estimates;
 * communicate them as "planning ranges, not bids" everywhere in the UI.
 */

// Category-level base ranges {min, max} for ROUGH professional repair exposure.
// Used by /tools/repair-exposure-calculator, the Negotiation Calculator's
// suggested discussion range, and Seller Credit Estimator's planning band.
export const CATEGORY_BASE_RANGES = {
    roof: { min: 1500, max: 18000 },
    foundation: { min: 3500, max: 30000 },
    structure: { min: 2500, max: 22000 },
    water_intrusion: { min: 800, max: 12000 },
    basement_water: { min: 1500, max: 18000 },
    plumbing: { min: 400, max: 9000 },
    electrical: { min: 500, max: 12000 },
    hvac: { min: 800, max: 14000 },
    windows_doors: { min: 600, max: 8000 },
    drainage: { min: 600, max: 9000 },
    mold: { min: 800, max: 8000 },
    pest: { min: 400, max: 10000 },
    safety: { min: 300, max: 6000 },
    other: { min: 300, max: 5000 },
};

// Multiplier applied based on severity selection.
export const SEVERITY_MULTIPLIER = {
    minor: { lo: 0.4, hi: 0.55 },
    moderate: { lo: 0.6, hi: 0.85 },
    major: { lo: 0.85, hi: 1.1 },
    unknown: { lo: 0.5, hi: 0.95 },
};

// Light adjustment based on declared urgency. Truly urgent issues skew slightly
// toward the upper bound (rush labor, after-hours, expedited materials).
export const URGENCY_MULTIPLIER = {
    immediate: { lo: 1.0, hi: 1.1 },
    "3_to_12_months": { lo: 0.9, hi: 1.0 },
    long_term: { lo: 0.85, hi: 1.0 },
    unknown: { lo: 0.9, hi: 1.0 },
};

// Home age can push exposure upward (older homes, hidden conditions).
export function ageMultiplier(homeAge) {
    if (homeAge == null || homeAge === "" || isNaN(Number(homeAge))) {
        return { lo: 1.0, hi: 1.0 };
    }
    const a = Number(homeAge);
    if (a < 10) return { lo: 0.9, hi: 1.0 };
    if (a < 25) return { lo: 1.0, hi: 1.0 };
    if (a < 50) return { lo: 1.0, hi: 1.1 };
    return { lo: 1.05, hi: 1.2 };
}

// Useful-life baselines for HVAC / roof age checks.
export const HVAC_USEFUL_LIFE = {
    furnace: 18,
    central_ac: 14,
    heat_pump: 14,
    boiler: 25,
    mini_split: 15,
    unknown: 15,
};

export const ROOF_USEFUL_LIFE = {
    asphalt: 22,
    flat: 18,
    metal: 40,
    tile: 45,
    unknown: 22,
};

// USD rough median (low/mid/high) for the roof tool. Per-square pricing
// intentionally NOT included — that would require local labor APIs.
export const ROOF_RANGES = {
    repair: { min: 400, max: 3500 },
    partial_replacement: { min: 3000, max: 12000 },
    full_replacement: { min: 8000, max: 35000 },
};

// HVAC replacement bands.
export const HVAC_RANGES = {
    service: { min: 200, max: 800 },
    major_repair: { min: 800, max: 3500 },
    replacement: { min: 4500, max: 18000 },
};

// Electrical concern bands (rough).
export const ELECTRICAL_RANGES = {
    minor_repairs: { min: 200, max: 1500 },
    panel_repair: { min: 800, max: 4000 },
    panel_replacement: { min: 2500, max: 7500 },
    rewire_partial: { min: 5000, max: 18000 },
};

// Foundation/water concern bands (rough).
export const FOUNDATION_RANGES = {
    monitor_seal: { min: 300, max: 2500 },
    crack_repair: { min: 800, max: 6000 },
    structural_review: { min: 1500, max: 5000 },
    major_repair: { min: 6000, max: 35000 },
};

export const WATER_INTRUSION_RANGES = {
    surface_cleanup: { min: 300, max: 1500 },
    drainage_fix: { min: 800, max: 6000 },
    waterproofing: { min: 3000, max: 15000 },
    interior_remediation: { min: 1500, max: 10000 },
};

/**
 * Compose a band {lo, hi} from a base category by applying severity, urgency,
 * and home age multipliers. Returns rounded integers in USD.
 */
export function adjustedBand(
    base,
    { severity = "unknown", urgency = "unknown", homeAge = null } = {},
) {
    if (!base) return null;
    const sev = SEVERITY_MULTIPLIER[severity] || SEVERITY_MULTIPLIER.unknown;
    const urg = URGENCY_MULTIPLIER[urgency] || URGENCY_MULTIPLIER.unknown;
    const age = ageMultiplier(homeAge);
    const lo = base.min * sev.lo * urg.lo * age.lo;
    const hi = base.max * sev.hi * urg.hi * age.hi;
    return {
        lo: Math.max(100, Math.round(lo / 50) * 50),
        hi: Math.max(200, Math.round(hi / 50) * 50),
    };
}

export function formatBand(band) {
    if (!band || band.lo == null || band.hi == null) return "—";
    const fmt = (n) =>
        n >= 1000 ? `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : `$${n}`;
    return `${fmt(band.lo)}–${fmt(band.hi)}`;
}

export function sumBands(bands) {
    if (!bands?.length) return null;
    return bands.reduce(
        (acc, b) => ({
            lo: acc.lo + (b?.lo || 0),
            hi: acc.hi + (b?.hi || 0),
        }),
        { lo: 0, hi: 0 },
    );
}
